import React, { FC, Ref, RefObject, createRef, useEffect, useMemo, useRef, useState } from 'react';
import { customerDetailsInterface, StepProps } from "../types/types";
import BookingService from "../api/BookingService";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from '../hooks';
import { setCustomerDetails } from '../stateReducers/customerDetailsSlice';
import { InvoiceForm } from './business/InvoiceForm';
import TextInputField from './business/TextInputField';
import Checkbox from './business/Checkbox';
import SelectInput from './business/SelectInput';
import { invalidInvoiceDetails, validMobilePhoneNumber } from './utils/stateHelper';
import { InvoiceDetailsInterface } from '../stateReducers/invoiceSlice';
import PhoneInputField from './business/PhoneInputField';
import { beneluxCountries, getBookingConfig } from '../data/price';
import { whichPromoInterval } from '../api/PromotionService';
  
interface Step5Props extends StepProps {
    signalHighlight: boolean
}

const Step5: FC<Step5Props> = ({setStepValidity, signalHighlight}) => {
    const d = useAppDispatch();
    const activePromo = useAppSelector((state) => { return state.delivery.activePromo; });
    const promoInUse = useMemo(() => {
        return activePromo !== "" && activePromo !== null;
    }, [activePromo]);
    const [promoBlocksCoupon, setPromoBlocksCoupon] = useState<boolean>(true);
    const customer = useAppSelector((state) => { return state.customerDetails; });
    const invoiceDetails = useAppSelector((state) => { return state.invoiceDetails; });
    const invalidInvoiceFields = invalidInvoiceDetails(invoiceDetails);
    const subscribed = useAppSelector((state) => { return state.customerDetails.subscribe; });
    const deliveryOptions = useAppSelector((state) => { return state.delivery; });

    const [highlightMode, setHighlightMode] = useState<boolean>(false);
    const [highlightList, setHighlightList] = useState<string[]>([]);
    const highlightField: (key: string) => boolean = (key: string) => {
        return highlightMode && highlightList.includes(key);
    }
    const bookingConfig = getBookingConfig();

    function translateFromFieldName(fieldName: string): string {
        // Deal with a couple exceptions
        if (fieldName === 'activity') {
            return t('customer.selectActivity');
        }
        if (fieldName === 'agreeCheckbox') {
            return "";
        }

        const customerPath = `customer.${fieldName}`;
        const invoicePath = `invoice.${fieldName}`;
        // If the translation result is the same as the path, then it didn't actually
        // find a translation. Check the translation "paths" that make sense, and return
        // the first one that works.
        if (t(customerPath) !== customerPath) {
            return t(customerPath);
        } else if (t(invoicePath) !== invoicePath) {
            return t(invoicePath);
        } else {
            // Not found. Print the missing name
            return fieldName;
        }
    }

    useEffect(() => {
        let newHighlightList: string[] = [];

        ['firstName', 'lastName', 'email', 'phone', 'activity'].forEach((key) => {
            if (customer[key as keyof customerDetailsInterface] === "") {
                newHighlightList.push(key);
            }
        })

        if (customer.activity == "youthCamps" && youthMovementName.length == 0) {
            newHighlightList.push("youthMovementName");
        }

        if (!customer.coupon && couponSelected) {
            newHighlightList.push('couponCode');
        }


        //if email contains @
        let emailValid = true;
        if (customer.email.includes("@")) {
            const email = customer.email;
            const emailParts = email.split("@");
            const domain = emailParts[emailParts.length-1];
            //if domain contains dot (.) - get top level domain
            if (domain?.includes(".")) {
                const domains = domain.split(".");
                const tld = domains[domains.length-1];
                //it should be 2 characters long at least
                emailValid = emailValid && !!tld && tld.length >= 2;
            } else {
                emailValid = false;
            }
        } else {
            emailValid = false;
        }
        if (!emailValid) {
            newHighlightList.push('email');
        }

        // Check phone number
        if (!validMobilePhoneNumber(customer.phone)) {
            newHighlightList.push('phone');
        }

        if (customer.needInvoice) {
            newHighlightList = newHighlightList.concat(invalidInvoiceFields)
        }

        if (!termsAccepted) {
            newHighlightList.push('agreeCheckbox')
        }

        if(newHighlightList.sort().join(',') !== highlightList.sort().join(',')){
            setHighlightList(newHighlightList);
        }
        setStepValidity(newHighlightList.length === 0);
    });

    useEffect(() => {
        if (signalHighlight === true) {
            setHighlightMode(true);
            // console.log("Signal triggered");
            // console.log("Highlight list: " + highlightList);
            // console.log("List: " + refList.current.keys());
            // const firstInvalidElem = refList[refIndexByLabel(highlightList[0])!];
            // console.log(firstInvalidElem);
            // if (firstInvalidElem) {
            //     firstInvalidElem.focus();
            // }
        }
    },[signalHighlight]);

    useEffect(() => {
        window.scrollTo(0, (document.getElementById('root')?.getBoundingClientRect().top ?? 0) + window.scrollY);
    }, []);

    const [couponSelected, setCouponSelected] = useState(customer.coupon !== undefined);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const activities = [
        'youth',
        'school',
        'students',
        'bachelor',
        'birthday',
        'family',
        'teambuilding',
        'youthCamps',
        'youthService',
        'other'
    ];

    const activitiesDict = () => {
        const dict = {
            // "": t('customer.selectActivity')
            "": "--"
        };
        activities.forEach((activity: string) => {
            dict[activity] = t('activity.' + activity);
        });
        return dict;
    }

    const selectActivity = (activity: string) => {
        d(setCustomerDetails({activity: activity}));
    }
    const {t} = useTranslation();

    const processCouponClick = (checked: boolean) => {
        setCouponSelected(checked);
        if (!checked) {
            d(setCustomerDetails({coupon: undefined}));
        }
    }

    const [couponState, setCouponState] = useState("");
    function setCouponCode(couponCode: string) {
        d(setCustomerDetails({
            coupon: couponCode
        }));
    }
    
    async function applyCouponCode() {
        setCouponState("");
        const response = await BookingService.handleCouponClick();
        // There is now a chance that bookingState has changed.
        setCouponState(response);
    }

    // Only allow invoice for customers in the Benelux unless they're doing a pickup at a rental point in Belgium
    const invoiceAllowed = beneluxCountries.includes(bookingConfig.country ?? 'be')
        || deliveryOptions.rentalPoint !== undefined;
    
    const [mustBeYouthCamp, setMustBeYouthCamp] = useState<boolean>(false);

    const language = useAppSelector((state) => { return state.globals.lang; });
    const formType = useAppSelector((state) => { return state.delivery.formType; });

    useEffect(() => {
        if (activePromo !== null && activePromo != "") {
            const promo = whichPromoInterval(deliveryOptions.playStart!, language, formType);
            if (promo && promo.customCodeHooks) { 
                if (promo.customCodeHooks.includes("youthCampOnly")) {
                    setMustBeYouthCamp(true);
                } else {
                    setMustBeYouthCamp(false);
                }

                if (promo.customCodeHooks.includes("combinableWithCoupons")) {
                    setPromoBlocksCoupon(false);
                } else {
                    setPromoBlocksCoupon(false);
                }

                return;
            }
        }
        // Defaults
        setMustBeYouthCamp(false);
        setPromoBlocksCoupon(true);
    }, [activePromo, customer.activity]);

    useEffect(() => {
        if (mustBeYouthCamp) {
            d(setCustomerDetails({
                activity: "youthCamps"
            }));
        }
    }, [mustBeYouthCamp]);

    const [youthMovementName, setyouthMovementName] = useState<string>("");

    const rowClasses = "form-group mb-2 flex flex-col lg:flex-row";

    return (
        <div>
            <div className="block max-w-full">
                <form>
                    <div className={rowClasses}>
                        <TextInputField
                            value={customer.firstName}
                            label="firstName"
                            labelText={t('customer.firstName')}
                            onChange={(e) => d(setCustomerDetails({firstName: e.target.value}))}
                            placeholder={t('customer.firstName')}
                            required={true}
                            additionalClassNames="lg:w-1/2 lg:pr-2"
                            redHighlight={highlightField('firstName')}
                            // refList={refList}
                            // refIndexByLabel={refIndexByLabel}
                        />
                        <TextInputField
                            value={customer.lastName}
                            label="lastName"
                            labelText={t('customer.lastName')}
                            onChange={(e) => d(setCustomerDetails({lastName: e.target.value}))}
                            placeholder={t('customer.lastName')}
                            required={true}
                            additionalClassNames="lg:w-1/2 lg:pr-2"
                            redHighlight={highlightField('lastName')}
                            // refList={refList}
                            // refIndexByLabel={refIndexByLabel}
                        />
                    </div>
                    <div className={rowClasses}>
                        <TextInputField
                            value={customer.email}
                            label="email"
                            labelText={t('customer.email')}
                            onChange={(e) => d(setCustomerDetails({email: e.target.value}))}
                            placeholder={t('customer.email')}
                            required={true}
                            redHighlight={highlightField('email')}
                            // refList={refList}
                            // refIndexByLabel={refIndexByLabel}
                        />
                    </div>
                    <div className={rowClasses}>
                        <PhoneInputField
                            value={customer.phone}
                            label="phone"
                            labelText={t('customer.phone')}
                            onChangeString={(phoneNumber) => d(setCustomerDetails({phone: phoneNumber}))}
                            placeholder={t('customer.phone')}
                            required={true}
                            redHighlight={highlightField('phone')}
                            additionalNote={validMobilePhoneNumber(customer.phone) ? undefined : t('customer.mobilePhoneNumberNote')}
                        />
                    </div>
                    <div className={rowClasses}>
                        <SelectInput
                            value={customer.activity ?? ""}
                            options={activitiesDict()}
                            label={'activity'}
                            labelText={t('customer.selectActivity')}
                            onChange={(e) => {
                                selectActivity(e.target.value)
                            }}
                            required={true}
                            redHighlight={highlightField('activity')}
                            inputDisabled={mustBeYouthCamp}
                        />
                    </div>
                        
                        {["youthCamps", "youth", "youthService"].includes(customer.activity) &&
                            <TextInputField
                                value={youthMovementName}
                                label="youthMovementName"
                                labelText={t('customer.youthMovementName')}
                                onChange={(e) => { setyouthMovementName(e.target.value); }}
                                placeholder={t('customer.youthMovementName')}
                                required={true}
                                redHighlight={highlightField('youthMovementName')}
                                
                            />
                        }
                    
                    <div className={rowClasses}>
                        {promoInUse && promoBlocksCoupon &&
                            <div className={rowClasses}>
                                <p style={{
                                    fontSize: '1.2em',
                                    marginBottom: '0px'
                                }}>{t('customer.couponUnavailable')}</p>
                            </div>
                        }
                    </div>
                    <div className={rowClasses}>
                        <Checkbox
                            checked={couponSelected}
                            label="promoCheckbox"
                            labelText={t('promoCheckboxLabel')}
                            // @ts-ignore
                            onChange={(e) => processCouponClick(e.target.checked)}
                            required={false}
                            redHighlight={highlightField('promoCheckbox')}
                            inputDisabled={promoInUse && promoBlocksCoupon}
                        />
                    </div>
                    
                    {couponSelected &&
                        <>
                            <div className={rowClasses}>
                                <TextInputField
                                    value={customer.coupon ?? ""}
                                    label="coupon"
                                    labelText={t('customer.coupon')}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder={t('customer.coupon')}
                                    required={true}
                                    redHighlight={highlightField('coupon')}
                                />
                            </div>
                            <div className={rowClasses}>
                                <button onClick={
                                    (e) => {
                                        e.preventDefault();
                                        applyCouponCode() 
                                    }}>Submit</button>
                            </div>
                            {(couponState === 'Error applying coupon') &&
                                <div className={rowClasses}>
                                    <div className="mb-3 w-full">
                                        <div className="form-check-input border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300">
                                            {t('couponStates.error')}
                                        </div>
                                    </div>
                                </div>
                            }
                            {couponState === 'Extras added' &&
                                <div className={rowClasses}>
                                    <div className="mb-3 w-full">
                                        <div className="form-check-input border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300">
                                            {t('couponStates.extraAdded')}
                                        </div>
                                    </div>
                                </div>
                            }
                            {couponState === 'No change' &&
                                <div className={rowClasses}>
                                    <div className="mb-3 w-full">
                                        <div className="form-check-input border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300">
                                            {t('couponStates.noChange')}
                                        </div>
                                    </div>
                                </div>
                            }
                            {(couponState === 'Invalid coupon' || couponState === 'Unknown coupon code') &&
                                <div className={rowClasses}>
                                    <div className="mb-3 w-full">
                                        <div className="form-check-input border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300">
                                            {t('couponStates.invalidCoupon')}
                                        </div>
                                    </div>
                                </div>
                            }
                            {couponState === 'Not enough stock' &&
                                <div className={rowClasses}>
                                    <div className="mb-3 w-full">
                                        <div className="form-check-input border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300">
                                            {t('couponStates.notEnoughStock')}
                                        </div>
                                    </div>
                                </div>
                            }
                        </>
                    }
                    {invoiceAllowed && <div className={rowClasses}>
                        <Checkbox
                            checked={customer.needInvoice ?? false}
                            label="invoiceCheckbox"
                            labelText={t('needInvoiceLabel')}
                            // @ts-ignore
                            onChange={(e) => d(setCustomerDetails({needInvoice: e.target.checked}))}
                            required={false}
                            redHighlight={highlightField('invoiceCheckbox')}
                        />
                    </div>
                    }
                    {invoiceAllowed && customer.needInvoice &&
                        <InvoiceForm highlightField={highlightField} />
                    }
                    <div className={rowClasses}>
                        <Checkbox
                            checked={subscribed}
                            label="subscribedCheckbox"
                            labelText={t('customer.subscription', {interpolation: {escapeValue: false}})}
                            useLabelTextAsHtml={true}
                            // @ts-ignore
                            onChange={(e) => d(setCustomerDetails({subscribe: e.target.checked}))}
                            required={false}
                            redHighlight={highlightField('subscribedCheckbox')}
                        />
                    </div>
                    <div className={rowClasses}>
                        <Checkbox
                            checked={termsAccepted}
                            label="agreeCheckbox"
                            labelText={"<span>" + t('customer.termsLabel', {interpolation: {escapeValue: false}}) + "</span>"}
                            useLabelTextAsHtml={true}
                            // @ts-ignore
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            required={true}
                            redHighlight={highlightField('agreeCheckbox')}
                        />
                    </div>
                    {highlightMode === true &&
                        <>
                            <h4 style={{color: 'red'}}>{t('customer.invalidFields')}</h4>
                            {/* Skip empty strings. Currently used for terms and conditions checkbox */}
                            {highlightList.filter((str) => {return str !== ""}).map((fieldName) => {
                                return (<p>
                                    <span style={{color: 'red'}}>
                                        {translateFromFieldName(fieldName)}
                                    </span>
                                </p>);
                            })
                            }
                        </>
                    }
                </form>
            </div>
        </div>
    );
};

export default Step5;
